import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Chip,
  Skeleton,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../../context/UserContext';
import PageContainer from '../../components/page-container/PageContainer';
import StatusBadge from '../../components/status-badge/StatusBadge';
import { StatCardSkeleton, ListSkeleton } from '../../components/skeleton';
import ProjectApi from '../../services/ProjectApi';
import DocumentApi from '../../services/DocumentApi';
import InvitationApi from '../../services/InvitationApi';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const StatCard = ({ icon, label, value, loading, color = 'primary.main', onClick }) => {
  if (loading) {
    return <StatCardSkeleton height={140} />;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        minHeight: 140,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        } : {},
      }}
      onClick={onClick}
    >
      <Box sx={{ color, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {label}
      </Typography>
    </Paper>
  );
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalDocuments: 0,
    pendingInvitations: 0,
    totalConsumers: 0,
    pendingPayment: 0,
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [actionRequired, setActionRequired] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return;

      const projectApi = new ProjectApi();
      const documentApi = new DocumentApi();
      const invitationApi = new InvitationApi();

      try {
        const [projectsRes, documentsRes, pendingRes, consumersRes, recentRes] = await Promise.all([
          projectApi.totalUserProjects(user.uid),
          documentApi.getDocumentCountByUserId(user.uid),
          invitationApi.getPendingInvitationCount(user.uid),
          invitationApi.getConsumerCountByUserId(user.uid),
          projectApi.getUserProjects(user.uid, null, 5, 1),
        ]);

        // Get documents requiring action (unpaid with views)
        // Helper to normalize legacy status values
        const normalizeStatus = (status) => {
          if (status === 'uploaded' || status === 'viewed') return 'unpaid';
          if (status === 'downloaded') return 'paid';
          return status || 'unpaid';
        };

        const allProjectsRes = await projectApi.getUserProjects(user.uid, null, 100, 1);
        let pendingPaymentCount = 0;
        const docsNeedingAction = [];

        if (allProjectsRes.success && allProjectsRes.projects) {
          for (const project of allProjectsRes.projects.slice(0, 10)) {
            const docsRes = await documentApi.getDocumentsByProjectId(project.id);
            if (docsRes.success && docsRes.data) {
              // Filter for unpaid documents that have been viewed
              const viewedUnpaidDocs = docsRes.data.filter(d => {
                const status = normalizeStatus(d.status);
                const viewCount = d.viewCount || 0;
                return status === 'unpaid' && viewCount > 0;
              });
              pendingPaymentCount += viewedUnpaidDocs.length;
              viewedUnpaidDocs.slice(0, 3).forEach(doc => {
                if (docsNeedingAction.length < 5) {
                  docsNeedingAction.push({ ...doc, projectName: project.name });
                }
              });
            }
          }
        }

        setStats({
          totalProjects: projectsRes.success ? projectsRes.totalCount : 0,
          totalDocuments: documentsRes.success ? documentsRes.count : 0,
          pendingInvitations: pendingRes.success ? pendingRes.count : 0,
          totalConsumers: consumersRes.success ? consumersRes.count : 0,
          pendingPayment: pendingPaymentCount,
        });

        if (recentRes.success) {
          setRecentProjects(recentRes.projects);
        }

        setActionRequired(docsNeedingAction);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.uid]);

  if (!user?.uid) {
    return (
      <PageContainer>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="text" width={400} height={24} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[...Array(5)].map((_, index) => (
            <Grid size={{ xs: 6, sm: 6, md: 2.4 }} key={index}>
              <StatCardSkeleton height={140} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <ListSkeleton items={5} showAvatar={true} />
          </Grid>
        </Grid>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {getGreeting()}, {user?.name || t('dashboard.producer')}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('dashboard.welcome_message')}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            icon={<FolderIcon sx={{ fontSize: 40 }} />}
            label={t('dashboard.total_projects')}
            value={stats.totalProjects}
            loading={loading}
            onClick={() => navigate('/project')}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            icon={<DescriptionIcon sx={{ fontSize: 40 }} />}
            label={t('dashboard.total_documents')}
            value={stats.totalDocuments}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            icon={<PaymentIcon sx={{ fontSize: 40 }} />}
            label={t('dashboard.pending_payment')}
            value={stats.pendingPayment}
            loading={loading}
            color="warning.main"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            icon={<PendingActionsIcon sx={{ fontSize: 40 }} />}
            label={t('dashboard.pending_invitations')}
            value={stats.pendingInvitations}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            label={t('dashboard.total_consumers')}
            value={stats.totalConsumers}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Projects */}
        <Grid size={{ xs: 12, md: actionRequired.length > 0 ? 6 : 8 }}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('dashboard.recent_projects')}</Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/project')}
              >
                {t('dashboard.view_all')}
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {loading ? (
              <ListSkeleton items={5} showAvatar={true} inPaper={false} />
            ) : recentProjects.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('dashboard.no_projects')}
              </Typography>
            ) : (
              <List disablePadding>
                {recentProjects.map((project, index) => (
                  <Box key={project.id}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => navigate(`/project/${project.id}`)}>
                        <Box
                          sx={{
                            mr: 2,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FolderIcon />
                        </Box>
                        <ListItemText
                          primary={project.name}
                          secondary={project.description}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < recentProjects.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Action Required Section */}
        {actionRequired.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={2} sx={{ p: 3, height: '100%', borderLeft: 4, borderColor: 'warning.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VisibilityIcon color="warning" />
                <Typography variant="h6">{t('dashboard.action_required')}</Typography>
                <Chip label={actionRequired.length} color="warning" size="small" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('dashboard.action_required_description')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                {actionRequired.map((doc, index) => (
                  <Box key={doc.id}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => navigate(`/project/${doc.projectId}`)}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {doc.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doc.projectName}
                          </Typography>
                        </Box>
                        <StatusBadge status="unpaid" size="small" />
                      </ListItemButton>
                    </ListItem>
                    {index < actionRequired.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: actionRequired.length > 0 ? 12 : 4 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('dashboard.quick_actions')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: actionRequired.length > 0 ? 'row' : 'column' }, gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth={actionRequired.length === 0}
                onClick={() => navigate('/project')}
              >
                {t('dashboard.create_project')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<FolderIcon />}
                fullWidth={actionRequired.length === 0}
                onClick={() => navigate('/project')}
              >
                {t('dashboard.view_all_projects')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default DashboardPage;
